import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '../utils/apiConfig';
import { useMasterData } from '../contexts/MasterDataContext';
import { DashboardLayout } from '../components/DashboardLayout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import {
  Megaphone, Sparkles, Send, Users, TrendingUp, IndianRupee, Target,
  Star, Loader2, Search, MessageSquare, Zap, Activity, ArrowUp, ArrowDown, ChevronsUpDown,
  Copy, Download,
} from 'lucide-react';

const CAMPAIGN_TYPES = [
  { id: 'low_occupancy', label: 'Low Occupancy', icon: Zap },
  { id: 'venue', label: 'Venue-wise', icon: Target },
  { id: 'game', label: 'Game-wise', icon: Activity },
  { id: 'day', label: 'Day-wise', icon: Activity },
  { id: 'time', label: 'Time-wise', icon: Activity },
  { id: 'inactive', label: 'Inactive', icon: Users },
  { id: 'frequent', label: 'Frequent', icon: Star },
  { id: 'high_value', label: 'High Value', icon: IndianRupee },
];
const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const TIME_BUCKETS = ['6-8 AM', '8-10 AM', '10-12 PM', '12-4 PM', '4-6 PM', '6-8 PM', '8-10 PM', '10 PM+'];

const Stars = ({ score }) => {
  const filled = Math.round((score || 0) / 20);
  return (
    <span className="inline-flex items-center gap-0.5" title={`${score}%`}>
      {[1, 2, 3, 4, 5].map((i) => (
        <Star key={i} className={`w-3.5 h-3.5 ${i <= filled ? 'fill-amber-400 text-amber-400' : 'text-slate-300'}`} />
      ))}
      <span className="ml-1 text-xs font-semibold text-slate-700">{score}%</span>
    </span>
  );
};

const TONES = {
  indigo: 'bg-indigo-100 text-indigo-600',
  violet: 'bg-violet-100 text-violet-600',
  blue: 'bg-blue-100 text-blue-600',
  emerald: 'bg-emerald-100 text-emerald-600',
  amber: 'bg-amber-100 text-amber-600',
  rose: 'bg-rose-100 text-rose-600',
};

const Kpi = ({ icon: Icon, label, value, tone = 'indigo', testid }) => {
  const [bg, text] = (TONES[tone] || TONES.indigo).split(' ');
  return (
    <Card className="p-4" data-testid={testid}>
      <div className="flex items-center gap-3">
        <div className={`p-2.5 rounded-xl ${bg}`}><Icon className={`w-5 h-5 ${text}`} /></div>
        <div>
          <p className="text-xs text-slate-500">{label}</p>
          <p className="text-xl font-bold text-slate-900">{value}</p>
        </div>
      </div>
    </Card>
  );
};

const RecSortTh = ({ label, sortKey, recSort, onSort, className = '' }) => {
  const active = recSort.key === sortKey;
  const Icon = !active ? ChevronsUpDown : recSort.direction === 'asc' ? ArrowUp : ArrowDown;
  return (
    <th className={`p-2 ${className}`}>
      <button onClick={() => onSort(sortKey)} className="inline-flex items-center gap-1 hover:text-slate-900 select-none" data-testid={`rec-sort-${sortKey}`}>
        {label}
        <Icon className={`w-3 h-3 ${active ? 'text-indigo-600' : 'text-slate-400'}`} />
      </button>
    </th>
  );
};

export default function MarketingPage() {
  const { getVenueNames, allGames } = useMasterData();
  const venues = getVenueNames ? getVenueNames() : [];

  const [campaignType, setCampaignType] = useState('venue');
  const [filters, setFilters] = useState({
    venue: '', game: '', day: '', time_bucket: '', source_platform: '',
    inactive_days: '', min_frequency: '', min_revenue: '', max_revenue: '', min_score: '', top_n: '',
  });
  const STANDARD_PLATFORMS = ['App', 'WhatsApp', 'Online', 'Khelomore', 'District', 'Hudle', 'Walk-in', 'Calling', 'Direct'];
  const [sourceOptions, setSourceOptions] = useState(STANDARD_PLATFORMS);
  const [loading, setLoading] = useState(false);
  const [recs, setRecs] = useState([]);
  const [recSort, setRecSort] = useState({ key: 'probability_score', direction: 'desc' });
  const [stats, setStats] = useState(null);
  const [selected, setSelected] = useState({});
  const [kpis, setKpis] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [rules, setRules] = useState([]);
  const [scheduleAt, setScheduleAt] = useState('');
  const [ruleForm, setRuleForm] = useState(null);
  const [ruleSaving, setRuleSaving] = useState(false);

  // low occupancy
  const [loSlots, setLoSlots] = useState([]);
  const [loLoading, setLoLoading] = useState(false);
  const [fillLoading, setFillLoading] = useState(false);
  const [todaySlots, setTodaySlots] = useState([]);

  // compose dialog
  const [composeOpen, setComposeOpen] = useState(false);
  const [tmpl, setTmpl] = useState({ template_name: '', body: '', param_tokens: [], offer: '', booking_link: 'https://play.makspickleball.com', cooldown_days: 7 });
  const [testMobile, setTestMobile] = useState('');
  const [sending, setSending] = useState(false);

  const fetchDashboard = useCallback(async () => {
    try {
      const [d, c, dr, ru] = await Promise.all([
        axios.get(`${API}/marketing/dashboard`),
        axios.get(`${API}/marketing/campaigns?limit=50`),
        axios.get(`${API}/marketing/campaigns/drafts`),
        axios.get(`${API}/marketing/automation-rules`),
      ]);
      setKpis(d.data.kpis);
      setCampaigns(c.data.campaigns || []);
      setDrafts(dr.data.drafts || []);
      setRules(ru.data.rules || []);
    } catch (e) { /* noop */ }
  }, []);

  const fetchTemplates = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/marketing/templates`);
      const t = (r.data.templates || [])[0];
      if (t) setTmpl((prev) => ({ ...prev, template_name: t.template_name, body: t.body, param_tokens: t.param_tokens || [] }));
    } catch (e) { /* noop */ }
  }, []);

  useEffect(() => { fetchDashboard(); fetchTemplates(); }, [fetchDashboard, fetchTemplates]);

  const findPlayers = async () => {
    setLoading(true);
    setSelected({});
    try {
      const params = { campaign_type: campaignType };
      if (filters.venue) params.venue = filters.venue;
      if (filters.game) params.game = filters.game;
      if (filters.day !== '') params.day = DAYS.indexOf(filters.day);
      if (filters.time_bucket) params.time_bucket = filters.time_bucket;
      if (filters.source_platform) params.source_platform = filters.source_platform;
      if (filters.inactive_days) params.inactive_days = Number(filters.inactive_days);
      if (filters.min_frequency) params.min_frequency = Number(filters.min_frequency);
      if (filters.min_revenue) params.min_revenue = Number(filters.min_revenue);
      if (filters.max_revenue) params.max_revenue = Number(filters.max_revenue);
      if (filters.min_score) params.min_score = Number(filters.min_score);
      if (filters.top_n) params.top_n = Number(filters.top_n);
      const r = await axios.get(`${API}/marketing/recommendations`, { params });
      setRecs(r.data.recommendations || []);
      setStats(r.data.stats);
      if (Array.isArray(r.data.source_options) && r.data.source_options.length) {
        setSourceOptions((prev) => Array.from(new Set([...STANDARD_PLATFORMS, ...r.data.source_options])));
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to fetch recommendations');
    } finally {
      setLoading(false);
    }
  };

  const scanLowOccupancy = async () => {
    setLoLoading(true);
    try {
      const r = await axios.get(`${API}/marketing/low-occupancy-slots`, { params: { hours_ahead: 72, threshold: 40, venue: filters.venue || undefined } });
      setLoSlots(r.data.slots || []);
    } catch (e) {
      toast.error('Failed to scan slots');
    } finally {
      setLoLoading(false);
    }
  };

  const applyLoSlot = (s) => {
    setFilters((f) => ({ ...f, venue: s.venue, day: DAYS[s.weekday], time_bucket: s.time_bucket }));
    toast.success(`Targeting ${s.venue} · ${s.weekday_name} · ${s.time_bucket}`);
  };

  const fillDay = async (offset, labelWord) => {
    setFillLoading(offset);
    setSelected({});
    try {
      const r = await axios.get(`${API}/marketing/fill-today`, { params: { threshold: 60, top_per_slot: 15, day_offset: offset } });
      const recs = r.data.recommendations || [];
      setCampaignType('low_occupancy');
      setRecs(recs);
      setStats(r.data.stats);
      setTodaySlots(r.data.slots || []);
      const sel = {};
      recs.slice(0, 50).forEach((x) => { sel[x.player_mobile] = true; });
      setSelected(sel);
      if (recs.length === 0) {
        toast.info(`No low-occupancy slots ${labelWord} 🎉`);
      } else {
        toast.success(`${r.data.stats.slots_scanned} vacant windows ${labelWord} · ${recs.length} players · top ${Math.min(50, recs.length)} pre-selected`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || `Failed to scan ${labelWord}`);
    } finally {
      setFillLoading(false);
    }
  };

  const toggle = (mobile) => setSelected((s) => ({ ...s, [mobile]: !s[mobile] }));
  const selectAll = () => {
    const all = {}; recs.forEach((r) => { all[r.player_mobile] = true; }); setSelected(all);
  };
  const selectTop = (n) => {
    const top = {}; sortedRecs.slice(0, n).forEach((r) => { top[r.player_mobile] = true; }); setSelected(top);
  };
  const clearSel = () => setSelected({});
  const selectedRows = recs.filter((r) => selected[r.player_mobile]);

  // Format a mobile to country-code prefixed form: 91XXXXXXXXXX
  const formatNumber = (m) => {
    const ten = (m || '').replace(/\D/g, '').slice(-10);
    return ten.length === 10 ? `91${ten}` : '';
  };
  const selectedNumbersText = () =>
    selectedRows.map((r) => formatNumber(r.player_mobile)).filter(Boolean).join('\n');

  const copyNumbers = async () => {
    const txt = selectedNumbersText();
    if (!txt) { toast.error('Select at least one player'); return; }
    await navigator.clipboard.writeText(txt);
    toast.success(`Copied ${txt.split('\n').length} number(s)`);
  };
  const downloadNumbers = () => {
    const txt = selectedNumbersText();
    if (!txt) { toast.error('Select at least one player'); return; }
    const blob = new Blob([txt], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `whatsapp-numbers-${new Date().toISOString().slice(0, 10)}.txt`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
    toast.success(`Downloaded ${txt.split('\n').length} number(s)`);
  };

  const REC_ACCESSORS = {
    player_name: (r) => (r.player_name || '').toLowerCase(),
    preferred_venue: (r) => (r.preferred_venue || '').toLowerCase(),
    preferred_day: (r) => (r.preferred_day || '').toLowerCase(),
    last_booking_date: (r) => r.last_booking_date || '',
    total_bookings: (r) => r.total_bookings || 0,
    total_revenue: (r) => r.total_revenue || 0,
    probability_score: (r) => r.probability_score || 0,
  };
  const requestRecSort = (key) => setRecSort((p) => (p.key === key ? { key, direction: p.direction === 'asc' ? 'desc' : 'asc' } : { key, direction: 'desc' }));
  const sortedRecs = React.useMemo(() => {
    const acc = REC_ACCESSORS[recSort.key];
    if (!acc) return recs;
    const dir = recSort.direction === 'asc' ? 1 : -1;
    return [...recs].sort((a, b) => {
      const av = acc(a); const bv = acc(b);
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
  }, [recs, recSort]);

  const previewText = () => {
    const r = selectedRows[0] || recs[0];
    if (!r) return tmpl.body;
    return (tmpl.body || '')
      .replace(/{{PlayerName}}/g, r.player_name || 'there')
      .replace(/{{Venue}}/g, r.preferred_venue || filters.venue || 'MAKS')
      .replace(/{{Offer}}/g, tmpl.offer || '')
      .replace(/{{BookingLink}}/g, tmpl.booking_link || '');
  };

  const sendTest = async () => {
    if (!testMobile.match(/^\d{10}$/)) { toast.error('Enter a valid 10-digit test number'); return; }
    if (!tmpl.template_name) { toast.error('Approved template name is required'); return; }
    setSending(true);
    try {
      const r = await axios.post(`${API}/marketing/campaigns/test`, {
        mobile: testMobile, test_name: 'Test', template_name: tmpl.template_name,
        param_tokens: tmpl.param_tokens, venue: filters.venue, offer: tmpl.offer, booking_link: tmpl.booking_link,
      });
      toast[r.data.success ? 'success' : 'error'](r.data.success ? 'Test message sent' : 'Test failed');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Test failed');
    } finally { setSending(false); }
  };

  const dispatchCampaign = async (mode) => {
    if (!tmpl.template_name) { toast.error('Approved template name is required'); return; }
    if (selectedRows.length === 0) { toast.error('Select at least one player'); return; }
    if (mode === 'schedule' && !scheduleAt) { toast.error('Pick a date & time to schedule'); return; }
    setSending(true);
    try {
      const r = await axios.post(`${API}/marketing/campaigns/send`, {
        mode,
        scheduled_at: mode === 'schedule' ? new Date(scheduleAt).toISOString() : undefined,
        campaign_type: campaignType,
        template_name: tmpl.template_name,
        param_tokens: tmpl.param_tokens,
        offer: tmpl.offer, booking_link: tmpl.booking_link,
        venue: filters.venue, game: filters.game,
        day: filters.day !== '' ? DAYS.indexOf(filters.day) : null,
        time_bucket: filters.time_bucket,
        cooldown_days: Number(tmpl.cooldown_days || 0),
        recipients: selectedRows.map((x) => ({ mobile: x.player_mobile, name: x.player_name, venue: x.preferred_venue, score: x.probability_score, reason: x.reason })),
      });
      if (mode === 'send') toast.success(`Sent: ${r.data.sent} delivered, ${r.data.failed} failed, ${r.data.skipped_cooldown} skipped`);
      else if (mode === 'draft') toast.success('Saved as draft');
      else toast.success('Campaign scheduled');
      setComposeOpen(false);
      clearSel();
      fetchDashboard();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Action failed');
    } finally { setSending(false); }
  };

  const sendDraftNow = async (id) => {
    try {
      const r = await axios.post(`${API}/marketing/campaigns/${id}/send-now`);
      toast.success(`Sent: ${r.data.sent} delivered, ${r.data.failed} failed`);
      fetchDashboard();
    } catch (e) { toast.error(e.response?.data?.detail || 'Send failed'); }
  };

  const deleteDraft = async (id) => {
    try { await axios.delete(`${API}/marketing/campaigns/${id}`); fetchDashboard(); }
    catch (e) { toast.error('Delete failed'); }
  };

  const blankRule = () => ({
    name: '', campaign_type: 'low_occupancy', day_offset: 1, threshold: 60, top_n: 20,
    venue: '', game: '', inactive_days: '', min_score: 50,
    template_name: tmpl.template_name || 'maks_marketing_offer', offer: '', booking_link: 'https://play.makspickleball.com',
    cooldown_days: 7, auto_send: false, schedule: { type: 'daily', time: '10:00', days_of_week: [] },
  });

  const saveRule = async () => {
    if (!ruleForm.name) { toast.error('Rule name required'); return; }
    if (!ruleForm.template_name) { toast.error('Approved template name required'); return; }
    setRuleSaving(true);
    try {
      const payload = { ...ruleForm, venue: ruleForm.venue || null, game: ruleForm.game || null };
      if (ruleForm.id) await axios.put(`${API}/marketing/automation-rules/${ruleForm.id}`, payload);
      else await axios.post(`${API}/marketing/automation-rules`, payload);
      toast.success('Automation rule saved');
      setRuleForm(null);
      fetchDashboard();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setRuleSaving(false); }
  };

  const toggleRule = async (rule) => {
    try { await axios.put(`${API}/marketing/automation-rules/${rule.id}`, { enabled: !rule.enabled }); fetchDashboard(); }
    catch (e) { toast.error('Update failed'); }
  };

  const runRuleNow = async (id) => {
    try {
      const r = await axios.post(`${API}/marketing/automation-rules/${id}/run`);
      const res = r.data.result || {};
      toast.success(res.status === 'sent' ? `Sent to ${res.sent}` : `${res.matched} players → ${res.status}`);
      fetchDashboard();
    } catch (e) { toast.error(e.response?.data?.detail || 'Run failed'); }
  };

  const deleteRule = async (id) => {
    try { await axios.delete(`${API}/marketing/automation-rules/${id}`); fetchDashboard(); }
    catch (e) { toast.error('Delete failed'); }
  };

  const sendCampaign = () => dispatchCampaign('send');

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="marketing-page">
        <div className="flex items-center gap-3">
          <div className="p-3 rounded-xl bg-gradient-to-br from-fuchsia-500 to-indigo-600">
            <Megaphone className="w-7 h-7 text-white" />
          </div>
          <div>
            <h1 className="font-heading font-bold text-3xl text-slate-900 tracking-tight">Marketing</h1>
            <p className="text-slate-600 text-sm">AI-powered player recommendations &amp; WhatsApp campaigns</p>
          </div>
        </div>

        {/* One-tap fill today / tomorrow */}
        <Card className="p-4 bg-gradient-to-r from-indigo-600 to-fuchsia-600 border-0" data-testid="fill-today-card">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="text-white">
              <p className="font-heading font-semibold text-lg flex items-center gap-2"><Zap className="w-5 h-5" /> Fill Vacant Slots</p>
              <p className="text-white/80 text-sm">Scan low-occupancy windows and auto-pick the players most likely to book them.</p>
              {todaySlots.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {todaySlots.slice(0, 8).map((s, i) => (
                    <span key={i} className="text-xs bg-white/15 text-white px-2 py-0.5 rounded-full" data-testid={`today-slot-${i}`}>
                      {s.venue.replace('MAKS ', '')} · {s.time_bucket} ({s.occupancy_pct}%)
                    </span>
                  ))}
                </div>
              )}
            </div>
            <div className="flex gap-2 shrink-0">
              <Button onClick={() => fillDay(0, 'today')} disabled={fillLoading !== false} className="bg-white text-indigo-700 hover:bg-white/90" data-testid="fill-today-btn">
                {fillLoading === 0 ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Zap className="w-4 h-4 mr-2" />} Fill Today
              </Button>
              <Button onClick={() => fillDay(1, 'tomorrow')} disabled={fillLoading !== false} variant="outline" className="bg-transparent text-white border-white/60 hover:bg-white/10 hover:text-white" data-testid="fill-tomorrow-btn">
                {fillLoading === 1 ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Zap className="w-4 h-4 mr-2" />} Fill Tomorrow
              </Button>
            </div>
          </div>
        </Card>

        {/* KPIs */}
        {kpis && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="marketing-kpis">
            <Kpi icon={Send} label="Campaigns Sent" value={kpis.campaigns_sent} tone="indigo" testid="kpi-campaigns" />
            <Kpi icon={Users} label="Players Recommended" value={kpis.players_recommended} tone="violet" testid="kpi-recommended" />
            <Kpi icon={MessageSquare} label="Messages Delivered" value={kpis.messages_delivered} tone="blue" testid="kpi-delivered" />
            <Kpi icon={Target} label="Conversions" value={kpis.conversions} tone="emerald" testid="kpi-conversions" />
            <Kpi icon={IndianRupee} label="Revenue" value={`₹${kpis.revenue_generated}`} tone="amber" testid="kpi-revenue" />
            <Kpi icon={TrendingUp} label="Conv. Rate" value={`${kpis.conversion_rate}%`} tone="rose" testid="kpi-rate" />
          </div>
        )}

        <Tabs defaultValue="build">
          <TabsList data-testid="marketing-tabs">
            <TabsTrigger value="build" data-testid="tab-build">Build Campaign</TabsTrigger>
            <TabsTrigger value="drafts" data-testid="tab-drafts">Drafts &amp; Scheduled{drafts.length > 0 && <span className="ml-1 text-xs bg-amber-500 text-white rounded-full px-1.5">{drafts.length}</span>}</TabsTrigger>
            <TabsTrigger value="automation" data-testid="tab-automation">Automation</TabsTrigger>
            <TabsTrigger value="history" data-testid="tab-history">Campaign History</TabsTrigger>
          </TabsList>

          {/* ===== BUILD ===== */}
          <TabsContent value="build" className="space-y-4">
            {/* Campaign type */}
            <Card className="p-4">
              <Label className="text-sm text-slate-700">Campaign Type</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {CAMPAIGN_TYPES.map((t) => (
                  <button
                    key={t.id}
                    data-testid={`ctype-${t.id}`}
                    onClick={() => setCampaignType(t.id)}
                    className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${campaignType === t.id ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-slate-700 border-slate-300 hover:border-indigo-400'}`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>

              {campaignType === 'low_occupancy' && (
                <div className="mt-4 p-3 rounded-lg bg-amber-50 border border-amber-200">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium text-amber-800">Scan upcoming low-occupancy slots (next 72h, ≤40%)</p>
                    <Button size="sm" variant="outline" onClick={scanLowOccupancy} disabled={loLoading} data-testid="scan-lo-btn">
                      {loLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4 mr-1" />} Scan
                    </Button>
                  </div>
                  {loSlots.length > 0 && (
                    <div className="mt-2 max-h-40 overflow-auto space-y-1">
                      {loSlots.slice(0, 20).map((s, i) => (
                        <button key={i} onClick={() => applyLoSlot(s)} data-testid={`lo-slot-${i}`}
                          className="w-full text-left text-xs px-2 py-1.5 rounded bg-white hover:bg-amber-100 border border-amber-100 flex justify-between">
                          <span>{s.venue} · {s.weekday_name} {s.date.slice(5)} · {s.time_bucket}</span>
                          <span className="font-semibold text-amber-700">{s.occupancy_pct}%</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </Card>

            {/* Filters */}
            <Card className="p-4">
              <Label className="text-sm text-slate-700 flex items-center gap-2"><Search className="w-4 h-4" /> Filters</Label>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 mt-3">
                <div>
                  <Label className="text-xs text-slate-500">Venue</Label>
                  <Select value={filters.venue || 'all'} onValueChange={(v) => setFilters((f) => ({ ...f, venue: v === 'all' ? '' : v }))}>
                    <SelectTrigger data-testid="filter-venue"><SelectValue placeholder="All" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Venues</SelectItem>
                      {venues.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Game</Label>
                  <Select value={filters.game || 'all'} onValueChange={(v) => setFilters((f) => ({ ...f, game: v === 'all' ? '' : v }))}>
                    <SelectTrigger data-testid="filter-game"><SelectValue placeholder="All" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Games</SelectItem>
                      {(allGames || []).map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Day</Label>
                  <Select value={filters.day || 'all'} onValueChange={(v) => setFilters((f) => ({ ...f, day: v === 'all' ? '' : v }))}>
                    <SelectTrigger data-testid="filter-day"><SelectValue placeholder="All" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Any Day</SelectItem>
                      {DAYS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Time</Label>
                  <Select value={filters.time_bucket || 'all'} onValueChange={(v) => setFilters((f) => ({ ...f, time_bucket: v === 'all' ? '' : v }))}>
                    <SelectTrigger data-testid="filter-time"><SelectValue placeholder="All" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Any Time</SelectItem>
                      {TIME_BUCKETS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Platform</Label>
                  <Select value={filters.source_platform || 'all'} onValueChange={(v) => setFilters((f) => ({ ...f, source_platform: v === 'all' ? '' : v }))}>
                    <SelectTrigger data-testid="filter-platform"><SelectValue placeholder="All" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Platforms</SelectItem>
                      {sourceOptions.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Inactive ≥ days</Label>
                  <Input type="number" value={filters.inactive_days} onChange={(e) => setFilters((f) => ({ ...f, inactive_days: e.target.value }))} placeholder="e.g. 30" data-testid="filter-inactive" />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Min bookings</Label>
                  <Input type="number" value={filters.min_frequency} onChange={(e) => setFilters((f) => ({ ...f, min_frequency: e.target.value }))} placeholder="e.g. 3" data-testid="filter-frequency" />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Min revenue ₹</Label>
                  <Input type="number" value={filters.min_revenue} onChange={(e) => setFilters((f) => ({ ...f, min_revenue: e.target.value }))} data-testid="filter-minrev" />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Min score %</Label>
                  <Input type="number" value={filters.min_score} onChange={(e) => setFilters((f) => ({ ...f, min_score: e.target.value }))} placeholder="e.g. 50" data-testid="filter-minscore" />
                </div>
              </div>
              <div className="mt-3">
                <Button onClick={findPlayers} disabled={loading} className="bg-indigo-600 hover:bg-indigo-700" data-testid="find-players-btn">
                  {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />} Find Players
                </Button>
              </div>
            </Card>

            {/* Results */}
            {stats && (
              <Card className="p-4" data-testid="recommendations-card">
                <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                  <p className="text-sm text-slate-600">
                    <span className="font-semibold text-slate-900">{stats.returned}</span> of {stats.total_matched} matched · avg score {stats.avg_score}%
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <Button size="sm" variant="outline" onClick={selectAll} data-testid="select-all-btn">Select All</Button>
                    {[20, 50, 100, 250].map((n) => (
                      <Button key={n} size="sm" variant="outline" onClick={() => selectTop(n)} data-testid={`select-top-${n}`}>Top {n}</Button>
                    ))}
                    <Button size="sm" variant="ghost" onClick={clearSel} data-testid="clear-sel-btn">Clear</Button>
                  </div>
                </div>

                {/* Action bar — above the list */}
                <div className="flex flex-wrap items-center gap-2 mb-3 p-2 rounded-lg bg-slate-50 border">
                  <Badge variant="secondary" data-testid="selected-count">{selectedRows.length} selected</Badge>
                  <Button onClick={() => setComposeOpen(true)} disabled={selectedRows.length === 0} className="bg-emerald-600 hover:bg-emerald-700" data-testid="compose-btn">
                    <MessageSquare className="w-4 h-4 mr-2" /> Compose WhatsApp ({selectedRows.length})
                  </Button>
                  <Button variant="outline" onClick={copyNumbers} disabled={selectedRows.length === 0} data-testid="copy-numbers-btn">
                    <Copy className="w-4 h-4 mr-2" /> Copy Numbers
                  </Button>
                  <Button variant="outline" onClick={downloadNumbers} disabled={selectedRows.length === 0} data-testid="download-numbers-btn">
                    <Download className="w-4 h-4 mr-2" /> Download Numbers
                  </Button>
                  <span className="text-xs text-slate-400 ml-auto">Format: 91XXXXXXXXXX · one per line</span>
                </div>

                <div className="overflow-auto max-h-[480px] border rounded-lg">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 sticky top-0">
                      <tr className="text-left text-xs text-slate-500">
                        <th className="p-2 w-8"></th>
                        <RecSortTh label="Player" sortKey="player_name" recSort={recSort} onSort={requestRecSort} />
                        <RecSortTh label="Pref. Venue / Sport" sortKey="preferred_venue" recSort={recSort} onSort={requestRecSort} />
                        <RecSortTh label="Pref. Day / Time" sortKey="preferred_day" recSort={recSort} onSort={requestRecSort} />
                        <RecSortTh label="Last" sortKey="last_booking_date" recSort={recSort} onSort={requestRecSort} />
                        <RecSortTh label="Bk" sortKey="total_bookings" recSort={recSort} onSort={requestRecSort} className="text-right" />
                        <RecSortTh label="₹" sortKey="total_revenue" recSort={recSort} onSort={requestRecSort} className="text-right" />
                        <RecSortTh label="Score" sortKey="probability_score" recSort={recSort} onSort={requestRecSort} />
                        <th className="p-2">Reason</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedRecs.map((r) => (
                        <tr key={r.player_mobile} className="border-t hover:bg-slate-50" data-testid={`rec-row-${r.player_mobile}`}>
                          <td className="p-2">
                            <Checkbox checked={!!selected[r.player_mobile]} onCheckedChange={() => toggle(r.player_mobile)} data-testid={`rec-check-${r.player_mobile}`} />
                          </td>
                          <td className="p-2">
                            <div className="font-medium text-slate-900">{r.player_name || '—'}</div>
                            <div className="text-xs text-slate-500">{r.player_mobile}</div>
                          </td>
                          <td className="p-2 text-xs">{r.preferred_venue || '—'}<br /><span className="text-slate-500">{r.preferred_game || '—'}</span></td>
                          <td className="p-2 text-xs">{r.preferred_day || '—'}<br /><span className="text-slate-500">{r.preferred_time || '—'}</span></td>
                          <td className="p-2 text-xs text-slate-600">{r.last_booking_date || '—'}{r.recency_days != null && <div className="text-slate-400">{r.recency_days}d ago</div>}</td>
                          <td className="p-2 text-right">{r.total_bookings}</td>
                          <td className="p-2 text-right">{Math.round(r.total_revenue)}</td>
                          <td className="p-2"><Stars score={r.probability_score} /></td>
                          <td className="p-2 text-xs text-slate-600 max-w-[260px]">{r.reason}</td>
                        </tr>
                      ))}
                      {recs.length === 0 && (
                        <tr><td colSpan={9} className="p-6 text-center text-slate-400">No players matched. Adjust filters and try again.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </Card>
            )}
          </TabsContent>

          {/* ===== DRAFTS & SCHEDULED ===== */}
          <TabsContent value="drafts">
            <Card className="p-4" data-testid="drafts-card">
              <div className="overflow-auto border rounded-lg">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50">
                    <tr className="text-left text-xs text-slate-500">
                      <th className="p-2">Campaign</th>
                      <th className="p-2">Status</th>
                      <th className="p-2">Template</th>
                      <th className="p-2 text-right">Recipients</th>
                      <th className="p-2">Scheduled / Source</th>
                      <th className="p-2 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {drafts.map((c) => (
                      <tr key={c.id} className="border-t" data-testid={`draft-row-${c.id}`}>
                        <td className="p-2 font-medium text-slate-900">{c.name}</td>
                        <td className="p-2">
                          <Badge variant="outline" className={c.status === 'pending_review' ? 'border-amber-400 text-amber-700' : c.status === 'scheduled' ? 'border-blue-400 text-blue-700' : 'border-slate-300'}>
                            {c.status === 'pending_review' ? 'Review' : c.status}
                          </Badge>
                          {c.auto_triggered && <Badge variant="secondary" className="ml-1 text-xs">Auto</Badge>}
                        </td>
                        <td className="p-2 text-xs">{c.template_name}</td>
                        <td className="p-2 text-right">{c.target_count}</td>
                        <td className="p-2 text-xs text-slate-500">{c.scheduled_at ? new Date(c.scheduled_at).toLocaleString() : (c.auto_triggered ? 'Automation' : 'Manual draft')}</td>
                        <td className="p-2 text-right whitespace-nowrap">
                          <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 mr-1" onClick={() => sendDraftNow(c.id)} data-testid={`draft-send-${c.id}`}>Send Now</Button>
                          <Button size="sm" variant="ghost" onClick={() => deleteDraft(c.id)} data-testid={`draft-del-${c.id}`}>Delete</Button>
                        </td>
                      </tr>
                    ))}
                    {drafts.length === 0 && (
                      <tr><td colSpan={6} className="p-6 text-center text-slate-400">No drafts, scheduled, or pending-review campaigns.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </Card>
          </TabsContent>

          {/* ===== AUTOMATION ===== */}
          <TabsContent value="automation" className="space-y-3">
            <div className="flex justify-end">
              <Button onClick={() => setRuleForm(blankRule())} className="bg-indigo-600 hover:bg-indigo-700" data-testid="new-rule-btn">
                <Zap className="w-4 h-4 mr-2" /> New Automation Rule
              </Button>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              {rules.map((r) => (
                <Card key={r.id} className="p-4" data-testid={`rule-card-${r.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="font-semibold text-slate-900">{r.name}</p>
                      <p className="text-xs text-slate-500 mt-0.5">
                        {r.campaign_type} · {r.schedule?.type === 'weekly' ? `Weekly ${(r.schedule.days_of_week || []).map((d) => DAYS[d].slice(0, 3)).join(',')}` : 'Daily'} @ {r.schedule?.time}
                      </p>
                      <div className="flex flex-wrap gap-1 mt-1.5">
                        <Badge variant={r.auto_send ? 'default' : 'outline'} className={r.auto_send ? 'bg-rose-600' : 'border-amber-400 text-amber-700'}>{r.auto_send ? 'Auto-send' : 'Review first'}</Badge>
                        {r.venue && <Badge variant="secondary" className="text-xs">{r.venue}</Badge>}
                        <Badge variant="secondary" className="text-xs">Top {r.top_n}</Badge>
                      </div>
                      {r.last_run && <p className="text-xs text-slate-400 mt-1">Last run: {new Date(r.last_run).toLocaleString()} → {r.last_result?.status}</p>}
                      <p className="text-xs text-slate-400">Next: {r.next_run ? new Date(r.next_run).toLocaleString() : '—'}</p>
                    </div>
                    <div className="flex flex-col items-end gap-1.5">
                      <button onClick={() => toggleRule(r)} data-testid={`rule-toggle-${r.id}`} className={`text-xs px-2 py-0.5 rounded-full ${r.enabled ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>{r.enabled ? 'Enabled' : 'Disabled'}</button>
                    </div>
                  </div>
                  <div className="flex gap-2 mt-3">
                    <Button size="sm" variant="outline" onClick={() => runRuleNow(r.id)} data-testid={`rule-run-${r.id}`}>Run Now</Button>
                    <Button size="sm" variant="ghost" onClick={() => setRuleForm({ ...r, venue: r.venue || '', game: r.game || '', inactive_days: r.inactive_days || '' })} data-testid={`rule-edit-${r.id}`}>Edit</Button>
                    <Button size="sm" variant="ghost" className="text-rose-600" onClick={() => deleteRule(r.id)} data-testid={`rule-del-${r.id}`}>Delete</Button>
                  </div>
                </Card>
              ))}
              {rules.length === 0 && (
                <Card className="p-6 text-center text-slate-400 lg:col-span-2">No automation rules yet. Create one to auto-fill vacant slots.</Card>
              )}
            </div>
          </TabsContent>

          {/* ===== HISTORY ===== */}
          <TabsContent value="history">
            <Card className="p-4" data-testid="history-card">
              <div className="overflow-auto border rounded-lg">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50">
                    <tr className="text-left text-xs text-slate-500">
                      <th className="p-2">Campaign</th>
                      <th className="p-2">Type</th>
                      <th className="p-2">Template</th>
                      <th className="p-2 text-right">Sent</th>
                      <th className="p-2 text-right">Conversions</th>
                      <th className="p-2 text-right">Conv %</th>
                      <th className="p-2 text-right">Revenue</th>
                      <th className="p-2">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {campaigns.map((c) => (
                      <tr key={c.id} className="border-t" data-testid={`campaign-row-${c.id}`}>
                        <td className="p-2 font-medium text-slate-900">{c.name}</td>
                        <td className="p-2"><Badge variant="outline">{c.campaign_type}</Badge></td>
                        <td className="p-2 text-xs">{c.template_name}</td>
                        <td className="p-2 text-right">{c.sent}</td>
                        <td className="p-2 text-right font-semibold text-emerald-600">{c.conversions}</td>
                        <td className="p-2 text-right">{c.conversion_rate}%</td>
                        <td className="p-2 text-right">₹{Math.round(c.revenue)}</td>
                        <td className="p-2 text-xs text-slate-500">{(c.created_at || '').slice(0, 10)}</td>
                      </tr>
                    ))}
                    {campaigns.length === 0 && (
                      <tr><td colSpan={8} className="p-6 text-center text-slate-400">No campaigns sent yet.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Compose dialog */}
        <Dialog open={composeOpen} onOpenChange={setComposeOpen}>
          <DialogContent className="max-w-2xl" data-testid="compose-dialog">
            <DialogHeader><DialogTitle>WhatsApp Campaign — {selectedRows.length} players</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Approved Template Name *</Label>
                  <Input value={tmpl.template_name} onChange={(e) => setTmpl((t) => ({ ...t, template_name: e.target.value }))} placeholder="maks_marketing_offer" data-testid="tmpl-name" />
                </div>
                <div>
                  <Label className="text-xs">Booking Link</Label>
                  <Input value={tmpl.booking_link} onChange={(e) => setTmpl((t) => ({ ...t, booking_link: e.target.value }))} data-testid="tmpl-link" />
                </div>
              </div>
              <div>
                <Label className="text-xs">Offer text ({'{{Offer}}'})</Label>
                <Input value={tmpl.offer} onChange={(e) => setTmpl((t) => ({ ...t, offer: e.target.value }))} placeholder="Flat 20% off this weekend!" data-testid="tmpl-offer" />
              </div>
              <div>
                <Label className="text-xs">Message body (template) — variables: {'{{PlayerName}} {{Venue}} {{Offer}} {{BookingLink}}'}</Label>
                <Textarea value={tmpl.body} onChange={(e) => setTmpl((t) => ({ ...t, body: e.target.value }))} rows={3} data-testid="tmpl-body" />
              </div>
              <div className="p-3 rounded-lg bg-slate-50 border">
                <p className="text-xs text-slate-500 mb-1">Preview (first recipient)</p>
                <p className="text-sm text-slate-800 whitespace-pre-wrap" data-testid="tmpl-preview">{previewText()}</p>
              </div>
              <div className="grid grid-cols-3 gap-3 items-end">
                <div className="col-span-2">
                  <Label className="text-xs">Send test to (10-digit)</Label>
                  <Input value={testMobile} onChange={(e) => setTestMobile(e.target.value)} placeholder="9876543210" data-testid="test-mobile" />
                </div>
                <Button variant="outline" onClick={sendTest} disabled={sending} data-testid="send-test-btn">
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Send Test'}
                </Button>
              </div>
              <div className="flex items-center gap-2">
                <Label className="text-xs">Cooldown (days)</Label>
                <Input type="number" className="w-24" value={tmpl.cooldown_days} onChange={(e) => setTmpl((t) => ({ ...t, cooldown_days: e.target.value }))} data-testid="cooldown-days" />
                <span className="text-xs text-slate-500">Skip players messaged within this window</span>
              </div>
              <div className="flex items-center gap-2">
                <Label className="text-xs">Schedule for</Label>
                <Input type="datetime-local" className="w-56" value={scheduleAt} onChange={(e) => setScheduleAt(e.target.value)} data-testid="schedule-at" />
                <span className="text-xs text-slate-500">(optional — for the Schedule button)</span>
              </div>
            </div>
            <DialogFooter className="flex-wrap gap-2">
              <Button variant="outline" onClick={() => setComposeOpen(false)}>Cancel</Button>
              <Button variant="outline" onClick={() => dispatchCampaign('draft')} disabled={sending} data-testid="save-draft-btn">Save Draft</Button>
              <Button variant="outline" onClick={() => dispatchCampaign('schedule')} disabled={sending} data-testid="schedule-btn">Schedule</Button>
              <Button onClick={() => dispatchCampaign('send')} disabled={sending} className="bg-emerald-600 hover:bg-emerald-700" data-testid="send-campaign-btn">
                {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />} Send Now
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Automation rule editor */}
        <Dialog open={!!ruleForm} onOpenChange={(o) => !o && setRuleForm(null)}>
          <DialogContent className="max-w-2xl" data-testid="rule-dialog">
            <DialogHeader><DialogTitle>{ruleForm?.id ? 'Edit' : 'New'} Automation Rule</DialogTitle></DialogHeader>
            {ruleForm && (
              <div className="space-y-3 max-h-[70vh] overflow-auto pr-1">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Rule name</Label>
                    <Input value={ruleForm.name} onChange={(e) => setRuleForm((r) => ({ ...r, name: e.target.value }))} placeholder="Fill tomorrow mornings" data-testid="rule-name" />
                  </div>
                  <div>
                    <Label className="text-xs">Campaign type</Label>
                    <Select value={ruleForm.campaign_type} onValueChange={(v) => setRuleForm((r) => ({ ...r, campaign_type: v }))}>
                      <SelectTrigger data-testid="rule-ctype"><SelectValue /></SelectTrigger>
                      <SelectContent>{CAMPAIGN_TYPES.map((t) => <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Schedule</Label>
                    <Select value={ruleForm.schedule.type} onValueChange={(v) => setRuleForm((r) => ({ ...r, schedule: { ...r.schedule, type: v } }))}>
                      <SelectTrigger data-testid="rule-sched-type"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="daily">Daily</SelectItem>
                        <SelectItem value="weekly">Weekly</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Time</Label>
                    <Input type="time" value={ruleForm.schedule.time} onChange={(e) => setRuleForm((r) => ({ ...r, schedule: { ...r.schedule, time: e.target.value } }))} data-testid="rule-time" />
                  </div>
                </div>
                {ruleForm.schedule.type === 'weekly' && (
                  <div>
                    <Label className="text-xs">Days of week</Label>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {DAYS.map((d, i) => (
                        <button key={d} type="button" data-testid={`rule-dow-${i}`}
                          onClick={() => setRuleForm((r) => {
                            const cur = r.schedule.days_of_week || [];
                            const next = cur.includes(i) ? cur.filter((x) => x !== i) : [...cur, i];
                            return { ...r, schedule: { ...r.schedule, days_of_week: next } };
                          })}
                          className={`px-2 py-1 rounded text-xs border ${(ruleForm.schedule.days_of_week || []).includes(i) ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-slate-600 border-slate-300'}`}>
                          {d.slice(0, 3)}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs">Venue (optional)</Label>
                    <Select value={ruleForm.venue || 'all'} onValueChange={(v) => setRuleForm((r) => ({ ...r, venue: v === 'all' ? '' : v }))}>
                      <SelectTrigger data-testid="rule-venue"><SelectValue placeholder="All" /></SelectTrigger>
                      <SelectContent><SelectItem value="all">All</SelectItem>{venues.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Day offset (low-occ)</Label>
                    <Input type="number" value={ruleForm.day_offset} onChange={(e) => setRuleForm((r) => ({ ...r, day_offset: Number(e.target.value) }))} data-testid="rule-offset" />
                  </div>
                  <div>
                    <Label className="text-xs">Top N players</Label>
                    <Input type="number" value={ruleForm.top_n} onChange={(e) => setRuleForm((r) => ({ ...r, top_n: Number(e.target.value) }))} data-testid="rule-topn" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Approved template name *</Label>
                    <Input value={ruleForm.template_name} onChange={(e) => setRuleForm((r) => ({ ...r, template_name: e.target.value }))} data-testid="rule-template" />
                  </div>
                  <div>
                    <Label className="text-xs">Offer text</Label>
                    <Input value={ruleForm.offer} onChange={(e) => setRuleForm((r) => ({ ...r, offer: e.target.value }))} data-testid="rule-offer" />
                  </div>
                </div>
                <div className="flex items-center justify-between p-3 rounded-lg border bg-slate-50">
                  <div>
                    <p className="text-sm font-medium text-slate-800">When this rule fires</p>
                    <p className="text-xs text-slate-500">Auto-send delivers immediately; otherwise it creates a pending list for your review.</p>
                  </div>
                  <button type="button" onClick={() => setRuleForm((r) => ({ ...r, auto_send: !r.auto_send }))} data-testid="rule-autosend"
                    className={`px-3 py-1.5 rounded-full text-sm font-medium ${ruleForm.auto_send ? 'bg-rose-600 text-white' : 'bg-amber-100 text-amber-700'}`}>
                    {ruleForm.auto_send ? 'Auto-send' : 'Review first'}
                  </button>
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setRuleForm(null)}>Cancel</Button>
              <Button onClick={saveRule} disabled={ruleSaving} className="bg-indigo-600 hover:bg-indigo-700" data-testid="rule-save-btn">
                {ruleSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null} Save Rule
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
